import { Button } from '@petsmart-ui/sparky';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDeviceType, useWindowSize } from '@libs/common-next';
import './HorizontalCarousel.scss';
const defaultBreakpoints: Record<number, {
  perView: number;
  gap: number;
}> = {
  1200: {
    perView: 6,
    gap: 64
  },
  1080: {
    perView: 5,
    gap: 64
  },
  960: {
    perView: 5,
    gap: 64
  },
  768: {
    perView: 4,
    gap: 16
  },
  560: {
    perView: 3,
    gap: 16
  },
  375: {
    perView: 3,
    gap: 16
  }
};
type CarouselProps = {
  className: string;
  children: React.ReactElement[];
  onLeftArrowClick: () => void;
  onRightArrowClick: () => void;
  onDragging: (x: boolean) => void;
  perViewDefault: number;
  breakpoints?: Record<number, {
    perView: number;
    gap: number;
  }>;
};
export function HorizontalCarousel({
  children,
  className,
  perViewDefault = 6,
  onLeftArrowClick,
  onRightArrowClick,
  breakpoints = defaultBreakpoints,
  onDragging
}: CarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [showNext, setShowNext] = useState(false);
  const [showPrev, setShowPrev] = useState(false);
  const [breakpoint, setBreakpoint] = useState({
    perView: perViewDefault,
    gap: 64
  });
  const width = useWindowSize();
  const {
    isMobile
  } = useDeviceType();
  const updateBreakpoints = useCallback(() => {
    let match = false;
    if (window.matchMedia) {
      for (const point in breakpoints) {
        if (window.matchMedia(`(max-width: ${point}px)`).matches) {
          const newBreakpoint = breakpoints[point];
          if (newBreakpoint.perView !== breakpoint.perView) {
            setBreakpoint(newBreakpoint);
          }
          match = true;
        }
      }
    }
    if (!match) {
      setBreakpoint({
        perView: perViewDefault,
        gap: 64
      });
    }
  }, [width]);
  const updateButtonVisibility = useCallback(() => {
    if (carouselRef?.current) {
      const {
        scrollWidth,
        clientWidth
      } = carouselRef.current;
      setShowNext(scrollWidth - clientWidth - -currentTranslate > 0);
      setShowPrev(currentTranslate < 0);
    }
  }, [currentTranslate]);
  const itemWidth = useMemo(() => {
    if (carouselRef?.current) {
      const {
        clientWidth: clientWidth_0
      } = carouselRef.current;
      if (clientWidth_0 && breakpoint?.perView) {
        const childWidth = clientWidth_0 / breakpoint.perView;
        return {
          minWidth: `${childWidth}px`
        };
      }
    }
    return {};
  }, [width]);
  useEffect(() => {
    updateButtonVisibility();
  }, [updateButtonVisibility, prevTranslate, carouselRef?.current?.scrollWidth]);
  useEffect(() => {
    updateBreakpoints();
  }, [updateBreakpoints]);
  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    setIsMouseDown(true);
    setStartX(e.pageX);
  };
  const handleMouseUp = () => {
    if (carouselRef.current) {
      setIsMouseDown(false);
      let calcTranslate = currentTranslate;
      const {
        scrollWidth: scrollWidth_0,
        clientWidth: clientWidth_1
      } = carouselRef.current;
      if (scrollWidth_0 - clientWidth_1 - -currentTranslate < 0) {
        calcTranslate = clientWidth_1 - scrollWidth_0;
        setCurrentTranslate(clientWidth_1 - scrollWidth_0);
      }
      if (currentTranslate === prevTranslate) {
        onDragging(false);
      }
      if (currentTranslate > 0) {
        calcTranslate = 0;
        setCurrentTranslate(0);
      }
      setPrevTranslate(calcTranslate);
    }
  };
  const handleMouseMove = (e_0: React.MouseEvent<HTMLDivElement>) => {
    if (isMouseDown) {
      const x = e_0.pageX;
      const walk = x - startX;
      if (Math.abs(e_0.clientX - startX) > 10) {
        onDragging(true);
        setCurrentTranslate(prevTranslate + walk);
      }
    }
  };
  const handleTouchStart = (e_1: React.TouchEvent<HTMLDivElement>) => {
    setIsMouseDown(true);
    onDragging(true);
    setStartX(e_1.touches[0].pageX);
  };
  const handleTouchMove = (e_2: React.TouchEvent<HTMLDivElement>) => {
    if (isMouseDown) {
      const x_0 = e_2.touches[0].pageX;
      const walk_0 = x_0 - startX;
      setCurrentTranslate(prevTranslate + walk_0);
    }
  };
  const handleEndEvent = () => {
    if (isMouseDown) {
      handleMouseUp();
    }
  };
  const handleNext = () => {
    if (carouselRef.current) {
      const {
        scrollWidth: scrollWidth_1,
        clientWidth: clientWidth_2
      } = carouselRef.current;
      setCurrentTranslate(clientWidth_2 - scrollWidth_1);
      setPrevTranslate(clientWidth_2 - scrollWidth_1);
      onRightArrowClick();
    }
  };
  const handlePrev = () => {
    setCurrentTranslate(0);
    setPrevTranslate(0);
    onLeftArrowClick();
  };
  const componentClassName = classnames('carousel-container', className);
  return <div aria-label="Image carousel" className={componentClassName}>
            {isMobile ? <div className="carousel-mobile" role="listbox">
                    {React.Children.map(children, (child, index) => <div className="carousel-outer-item" key={child?.key || index}>
                            {child}
                        </div>)}
                </div> : <>
                    <div id="swipe-carousel" role="listbox" className="carousel" tabIndex={0} ref={carouselRef} onMouseDown={handleMouseDown} onMouseLeave={handleEndEvent} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} onTouchStart={handleTouchStart} onTouchEnd={handleEndEvent} onTouchMove={handleTouchMove} style={{
        transform: `translate3d(${currentTranslate}px, 0, 0)`,
        transition: isMouseDown ? 'none' : 'transform 800ms cubic-bezier(0.165, 0.84, 0.44, 1)' // Smooth transition when not dragging
      }}>
                        <div className="carousel-inner">
                            {React.Children.map(children, (child_0, index_0) => <div className="carousel-outer-item" style={{
            gap: `${breakpoint?.gap}px`,
            ...itemWidth
          }} key={child_0?.key || index_0}>
                                    {child_0}
                                </div>)}
                        </div>
                    </div>

                    <div className="carousel-arrow-container">
                        {showPrev && <Button circle text="arrow-prev" aria-label="Previous slide" iconName="chevron-left" iconPosition="before" iconSize="md" hideText variant="primary" className="carousel-arrows carousel-arrow-left" data-testid="carousel-arrow-left" onClick={handlePrev} />}
                        {showNext && <Button circle text="arrow-next" aria-label="Next slide" iconName="chevron-right" iconPosition="before" iconSize="md" hideText variant="primary" className="carousel-arrows carousel-arrow-right" data-testid="carousel-arrow-right" onClick={handleNext} />}
                    </div>
                </>}
        </div>;
}